/**
 * One control that edits a member's value for any attribute type — used on the
 * member's own profile now, and by the member drawer next.
 */

import { focusRing, inputStyle } from '@/components/org/orgHelpers'
import { LANGUAGES } from '@/lib/languages'
import { DATE_MIN, dateMax, dateProblem, type AttrDef, type LocationValue, type Value } from '@/lib/attributesModel'
import { personItem, useAttrData } from '@/lib/attributesData'
import { MultiPick } from './shared'
import { LocationInput } from './LocationInput'

const small = { ...inputStyle, width: '100%', boxSizing: 'border-box' as const, padding: '9px 12px', fontSize: 14, borderRadius: 10 }
// `big`: 46px tall, to sit beside the read-only boxes on the full-page profile.
const large = { ...small, minHeight: 46, padding: '11px 14px', fontSize: 14.5 }

export function ValueInput({ def, value, onChange, big, cell }: { def: AttrDef; value: Value; onChange: (v: Value) => void; big?: boolean; cell?: (label: string, box: React.ReactNode) => React.ReactNode }) {
  const demo = useAttrData()
  const base = big ? large : small
  const label = def.label
  switch (def.type) {
    // Scrolling the page over a focused number box changed its value (Deepa's 4 became −11,
    // 7 Oct 2026) — the wheel now leaves the box instead.
    case 'number':
      return <input type="number" onWheel={e => e.currentTarget.blur()} aria-label={label} value={typeof value === 'number' ? value : ''} onChange={e => onChange(e.target.value === '' ? null : Number(e.target.value))} className={focusRing} style={base} />
    case 'date':
    {
      // Checked as it is picked (22 Oct 2026), so a bad date shows under the field, not at Send.
      const why = typeof value === 'string' && value ? dateProblem(def, value) : null
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <input type="date" min={DATE_MIN} max={dateMax(def)} aria-label={label} aria-invalid={!!why} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value || null)} className={focusRing} style={why ? { ...base, borderColor: '#B42318' } : base} />
          {why && <span role="alert" style={{ fontSize: 12.5, color: '#B42318' }}>{why.replace(`${label}: `, '').replace(/^./, c => c.toUpperCase())}</span>}
        </span>
      )
    }
    case 'yesno':
      return (
        <select aria-label={label} value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={e => onChange(e.target.value === '' ? null : e.target.value === 'yes')} className={`${focusRing} lb-select`} style={base}>
          <option value="">{`Select ${label.toLowerCase()}`}</option><option value="yes">Yes</option><option value="no">No</option>
        </select>
      )
    case 'option':
      return (
        <select aria-label={label} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value || null)} className={`${focusRing} lb-select`} style={base}>
          <option value="">{`Select ${label.toLowerCase()}`}</option>
          {(def.options ?? []).filter(o => !o.archived || o.id === value).map(o => <option key={o.id} value={o.id}>{o.label}{o.archived ? ' (archived)' : ''}</option>)}
        </select>
      )
    case 'multi':
      return <MultiPick items={(def.options ?? []).filter(o => !o.archived).map(o => ({ id: o.id, label: o.label }))} value={Array.isArray(value) ? value : []} onChange={v => onChange(v)} placeholder={`Pick ${label.toLowerCase()}…`} />
    case 'language':
      return (
        <select aria-label={label} value={typeof value === 'string' ? value : ''} onChange={e => onChange(e.target.value || null)} className={`${focusRing} lb-select`} style={base}>
          <option value="">{`Select ${label.toLowerCase()}`}</option>
          {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      )
    case 'person': {
      // A searchable picker, name over email (7 Oct 2026) — a plain <select> showed names only.
      const ids = def.personList ? demo.eligible[def.personList] : []
      const cur = typeof value === 'string' ? value : ''
      return (
        <MultiPick single label={label} items={[...new Set([...ids, ...(cur ? [cur] : [])])].map(id => personItem(demo, id))}
          value={cur ? [cur] : []} onChange={v => onChange(v[0] ?? null)} placeholder={`Select ${label.toLowerCase()}…`} />
      )
    }
    case 'location':
      return <LocationInput big={big} cell={cell} value={value && typeof value === 'object' && !Array.isArray(value) ? value as LocationValue : null} onChange={onChange} />
    default:
      return <input aria-label={label} value={typeof value === 'string' ? value : value == null ? '' : String(value)} onChange={e => onChange(e.target.value || null)} className={focusRing} style={base} />
  }
}
